import React, { useState } from "react";
import {
    AppBar,
    Toolbar,
    Button,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    MenuItem,
    Typography,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useNavigate } from "react-router-dom";
import { scroller } from "react-scroll";
import logoImg from "../images/trilliumLogo.png";

const Header = () => {
    const [isDrawerOpen, setDrawerOpen] = useState(false);
    const [isServicesHovered, setServicesHovered] = useState(false);
    const [servicesTimeout, setServicesTimeout] = useState(null);
    const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
    const navigate = useNavigate();

    const handleServicesMouseEnter = () => {
        clearTimeout(servicesTimeout);
        setServicesHovered(true);
    };

    const handleServicesMouseLeave = () => {
        const timeout = setTimeout(() => {
            setServicesHovered(false);
        }, 150); // Small delay for better UX
        setServicesTimeout(timeout);
    };

    const handleNavigation = (path) => {
        navigate(path);
        setServicesHovered(false);
    };

    const handleScrollToSection = (sectionId) => {
        if (window.location.pathname === "/") {
            // If we're on home page, scroll to section
            scroller.scrollTo(sectionId, {
                duration: 800,
                delay: 0,
                smooth: "easeInOutQuart",
            });
        } else {
            // If we're on another page, navigate to home first then scroll
            navigate("/");
            setTimeout(() => {
                scroller.scrollTo(sectionId, {
                    duration: 800,
                    delay: 100,
                    smooth: "easeInOutQuart",
                });
            }, 100);
        }
        setDrawerOpen(false);
    };

    const servicesMenuItems = [
        
        { label: "Defence Procurement", path: "" },
        { label: "Global Sourcing", path: "" },
        { label: "Supply Chain", path: "" },
        { label: "Quality Assurance", path: "" },
    ];

    const mainMenuItems = [
        { label: "About Us", action: () => handleScrollToSection("about-section") },
        {
            label: "Services",
            hasDropdown: true,
            items: servicesMenuItems
        },
        { label: "Industries" },
        { label: "Tender Opportunities" },
        { label: "Partners & Network" },
        { label: "Resources" },
        {
            label: "Contact",
            action: () => handleScrollToSection("contact-section")
        },
        // {
        //     label: "Blog",
        //     action: () => handleScrollToSection("blog-section")
        // },
        { label: "Log In", path: "/login" },
    ];

    return (
        <AppBar
            position="static"
            elevation={0}
            sx={{
                background: "transparent !important",
                boxShadow: "none !important",
                backdropFilter: "none",
                zIndex: 50, // z-50 for the entire AppBar
            }}
        >
            <Toolbar sx={{
                justifyContent: "space-between",
                paddingX: 2,
                background: "transparent",
                position: "relative",
                zIndex: 50,
            }}>

                {/* Logo Section */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        "& img": {
                            transition: "transform 0.8s ease",
                        },
                        "&:hover img": {
                            transform: "scale(1.1)",
                        },
                    }}
                    onClick={() => navigate("/")}
                >
                    <img
                        src={logoImg}
                        alt="Logo"
                        style={{ height: 100, }}
                    />
                </Box>

                {/* Desktop Navigation Links */}
                <Box
                    sx={{
                        display: { xs: "none", md: "flex" },
                        gap: 1,
                        color: "black",
                        flexWrap: "wrap",
                        fontWeight: "bold",
                        zIndex: 50,
                        "& .MuiButton-root": {
                            fontSize: "0.7rem",
                            fontWeight: "bold",
                            transition: "all 0.3s ease",

                            "&:hover": {
                                backgroundColor: "#0f758e73",
                                color: "white",
                                fontWeight: "bold",
                            },
                        },
                    }}
                >
                    {mainMenuItems.map((item) => (
                        item.hasDropdown ? (
                            <Box
                                key={item.label}
                                sx={{
                                    position: "relative",
                                    display: "inline-block"
                                }}
                                onMouseEnter={handleServicesMouseEnter}
                                onMouseLeave={handleServicesMouseLeave}
                            >
                                <Button
                                    color="inherit"
                                    endIcon={isServicesHovered ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    sx={{
                                        backgroundColor: isServicesHovered ? "#0f758e73" : "transparent",
                                        color: isServicesHovered ? "white" : "inherit",
                                    }}
                                >
                                    {item.label}
                                </Button>

                                {/* Dropdown Menu - Appears on hover */}
                                {isServicesHovered && (
                                    <Paper
                                        elevation={3}
                                        sx={{
                                            position: "absolute",
                                            top: "100%",
                                            left: 0,
                                            mt: 0.5,
                                            minWidth: 200,
                                            zIndex: 9999, // Very high z-index for dropdown
                                            backgroundColor: "#ffffffff",
                                            borderRadius: 1,
                                            overflow: "hidden",
                                        }}
                                        onMouseEnter={handleServicesMouseEnter}
                                        onMouseLeave={handleServicesMouseLeave}
                                    >
                                        {item.items.map((service) => (
                                            <MenuItem
                                                key={service.label}
                                                onClick={() => handleNavigation(service.path)}
                                                sx={{
                                                    py: 1.5,
                                                    px: 2,
                                                    fontSize: "0.8rem",
                                                    color: "black",
                                                    transition: "all 0.2s ease",
                                                    "&:hover": {
                                                        backgroundColor: "#0f758e73",
                                                        color: "white",
                                                    },
                                                }}
                                            >
                                                {service.label}
                                            </MenuItem>
                                        ))}
                                    </Paper>
                                )}
                            </Box>
                        ) : item.action ? (
                            <Button
                                key={item.label}
                                color="inherit"
                                onClick={item.action}
                            >
                                {item.label}
                            </Button>
                        ) : (
                            <Button
                                key={item.label}
                                color="inherit"
                                onClick={() => navigate(item.path)}
                            >
                                {item.label}
                            </Button>
                        )
                    ))}
                </Box>

                {/* Mobile Menu Icon */}
                <IconButton
                    edge="start"
                    aria-label="menu"
                    sx={{
                        display: { xs: "flex", md: "none" },
                        color: "black",
                        zIndex: 50,
                    }}
                    onClick={() => setDrawerOpen(true)}
                >
                    <MenuIcon />
                </IconButton>

                {/* Mobile Drawer */}
                <Drawer
                    anchor="right"
                    open={isDrawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    PaperProps={{
                        sx: {
                            zIndex: 9999, // High z-index for mobile drawer
                        }
                    }}
                >
                    <Box sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        p: 2,
                        backgroundColor: "#f5f5f5b7",
                        borderBottom: "1px solid #e0e0e0",
                    }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                            Menu
                        </Typography>
                        <IconButton onClick={() => setDrawerOpen(false)}>
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <List sx={{ width: 280 }}>
                        {mainMenuItems.map((item) => (
                            item.hasDropdown ? (
                                <React.Fragment key={item.label}>
                                    <ListItem
                                        button
                                        onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                                        sx={{
                                            borderBottom: "1px solid #f0f0f0",
                                            "&:hover": {
                                                backgroundColor: "#f8f8f8",
                                            }
                                        }}
                                    >
                                        <ListItemText
                                            primary={item.label}
                                            primaryTypographyProps={{
                                                fontWeight: 500,
                                            }}
                                        />
                                        {mobileServicesOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                    </ListItem>

                                    {mobileServicesOpen && (
                                        <Box sx={{
                                            backgroundColor: "#fafafa",
                                            borderLeft: "3px solid #0f758e73",
                                            ml: 1,
                                            mr: 1,
                                            mb: 1,
                                            borderRadius: "0 0 4px 4px",
                                            overflow: "hidden",
                                        }}>
                                            {item.items.map((service) => (
                                                <ListItem
                                                    key={service.label}
                                                    button
                                                    onClick={() => {
                                                        handleNavigation(service.path);
                                                        setMobileServicesOpen(false);
                                                        setDrawerOpen(false);
                                                    }}
                                                    sx={{
                                                        py: 1.5,
                                                        pl: 4,
                                                        "&:hover": {
                                                            backgroundColor: "#0f758e73",
                                                            "& .MuiTypography-root": {
                                                                color: "white",
                                                            }
                                                        },
                                                    }}
                                                >
                                                    <ListItemText
                                                        primary={service.label}
                                                        primaryTypographyProps={{
                                                            fontSize: "0.9rem",
                                                            color: "#555",
                                                        }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </Box>
                                    )}
                                </React.Fragment>
                            ) : item.action ? (
                                <ListItem
                                    key={item.label}
                                    button
                                    onClick={() => {
                                        item.action();
                                        setDrawerOpen(false);
                                    }}
                                    sx={{
                                        borderBottom: "1px solid #f0f0f0",
                                        "&:hover": {
                                            backgroundColor: "#f8f8f8",
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                        }}
                                    />
                                </ListItem>
                            ) : (
                                <ListItem
                                    key={item.label}
                                    button
                                    onClick={() => {
                                        handleNavigation(item.path);
                                        setDrawerOpen(false);
                                    }}
                                    sx={{
                                        borderBottom: "1px solid #f0f0f0",
                                        "&:hover": {
                                            backgroundColor: "#f8f8f8",
                                        }
                                    }}
                                >
                                    <ListItemText
                                        primary={item.label}
                                        primaryTypographyProps={{
                                            fontWeight: 500,
                                        }}
                                    />
                                </ListItem>
                            )
                        ))}
                    </List>
                </Drawer>
            </Toolbar>
        </AppBar>
    );
};

export default Header;